import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';
import URI from 'urijs';

export default class TraplogStore {
  // Authentication
  selectedDatabase = null;
  bearerToken = null;
  refreshBearerToken = null;

  setSelectedDatabase(selectedDatabase) {
    this.selectedDatabase = selectedDatabase;
  }

  setBearerToken(bearerToken) {
    this.bearerToken = bearerToken;
  }

  setRefreshBearerToken(refreshBearerToken) {
    this.refreshBearerToken = refreshBearerToken;
  }

  // Should only be loaded in the constructor
  loadingInsectGroups = false;
  insectGroups = [];

  loadingTrapLures = false;
  trapLures = [];

  loadingMatingDisruptionTies = false;
  matingDisruptionTies = [];

  loadingSprayProducts = false;
  sprayProducts = [];

  // Reloaded everytime the property is changed
  loadingTrappingZones = false;
  trappingZones = [];
  loadingTrappingResults = false;
  trappingResults = {};

  // User selections
  selectedInsectGroupId = null;
  selectedTrappingZoneId = null;

  constructor() {
    makeObservable(this, {
      loadingInsectGroups: observable,
      insectGroups: observable,
      loadingTrapLures: observable,
      trapLures: observable,
      loadingMatingDisruptionTies: observable,
      matingDisruptionTies: observable,
      loadingSprayProducts: observable,
      sprayProducts: observable,
      loadingTrappingZones: observable,
      trappingZones: observable,
      loadingTrappingResults: observable,
      trappingResults: observable,
      selectedInsectGroupId: observable,
      selectedTrappingZoneId: observable,

      loadInsectGroups: action,
      loadTrapLures: action,
      loadMatingDisruptionTies: action,
      loadSprayProducts: action,
      loadTrappingZones: action,
      loadTrappingResults: action,
      addTrap: action,
      setSelectedInsectGroupId: action,
      setSelectedTrappingZoneId: action,

      selectedInsectGroup: computed,
      selectedTrappingZone: computed,
      selectedTrappingZoneResults: computed,
      traps: computed
    });
  }

  /**
   * Fetches all insect groups from the traplog API.
   *
   * @param {string} bearerToken
   * @param {function} refreshBearerToken
   */
  loadInsectGroups(bearerToken, refreshBearerToken) {
    this.loadingInsectGroups = true;
    fetch(`${process.env.REACT_APP_TRAPLOG_API_URL}api/insect-groups`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + bearerToken
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          this.insectGroups = data;
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        this.insectGroups = [];
      })
      .finally(() => {
        this.loadingInsectGroups = false;
      });
  }

  /**
   * Fetches all trap lures from the traplog API.
   *
   * @param {string} bearerToken
   * @param {function} refreshBearerToken
   */
  loadTrapLures(bearerToken, refreshBearerToken) {
    this.loadingTrapLures = true;
    fetch(`${process.env.REACT_APP_TRAPLOG_API_URL}api/trap-lures`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + bearerToken
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          this.trapLures.replace(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        this.trapLures.clear();
      })
      .finally(() => {
        this.loadingTrapLures = false;
      });
  }

  /**
   * Fetches all mating disruption ties from the traplog API.
   *
   * @param {string} bearerToken
   * @param {function} refreshBearerToken
   */
  loadMatingDisruptionTies(bearerToken, refreshBearerToken) {
    this.loadingMatingDisruptionTies = true;
    fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/mating-disruption-ties`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + bearerToken
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          this.matingDisruptionTies.replace(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        this.matingDisruptionTies.clear();
      })
      .finally(() => {
        this.loadingMatingDisruptionTies = false;
      });
  }

  /**
   * Fetches all spray products from the traplog API.
   */
  loadSprayProducts() {
    this.loadingSprayProducts = true;
    fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/spray-products?with[]=product`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${this.bearerToken}`
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) this.refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          this.sprayProducts.replace(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        this.sprayProducts.clear();
      })
      .finally(() => {
        this.loadingSprayProducts = false;
      });
  }

  /**
   * Fetches all trapping zones for the supplied property from the traplog API.
   *
   * If the current selected trapping zone is invalid it will clear this.
   *
   * @param {string} bearerToken
   * @param {function} refreshBearerToken
   * @param {string} selectedDatabase
   * @param {int} propertyId
   */
  loadTrappingZones(
    bearerToken,
    refreshBearerToken,
    selectedDatabase,
    propertyId
  ) {
    this.loadingTrappingZones = true;
    fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/properties/${propertyId}/trapping-zones?with[]=traps.trapLure.insects&with[]=matingDisruptionTie.insects`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: 'Bearer ' + bearerToken,
          SeasonalDatabase: selectedDatabase
        }
      }
    )
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          // Replace the trapping zones data
          this.trappingZones.replace(data);

          // If there are any trapping zones and an invalid option is
          // already selected then select the first in the list.
          if (data.length > 0) {
            if (
              this.selectedTrappingZoneId !== null &&
              !data.some(
                (trappingZone) =>
                  trappingZone.id === this.selectedTrappingZoneId
              )
            )
              this.selectedTrappingZoneId = data[0].id;
          }

          // If there are no trapping zones then clear the
          // selected trapping zone.
          else this.selectedTrappingZoneId = null;
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        this.trappingZones.clear();
        this.selectedTrappingZoneId = null;
      })
      .finally(() => {
        this.trappingZones = this.trappingZones.map((tz) => ({
          ...tz,
          high_pressure: false
        }));
        this.loadTrappingResults(
          bearerToken,
          refreshBearerToken,
          selectedDatabase
        );
        this.loadingTrappingZones = false;
      });
  }

  /**
   * Fetches trapping results for all loaded trapping zones from the traplog API.
   *
   * @param {string} bearerToken
   * @param {function} refreshBearerToken
   * @param {string} selectedDatabase
   */
  loadTrappingResults(bearerToken, refreshBearerToken, selectedDatabase) {
    if (
      !this.trappingZones ||
      this.trappingZones.length < 1 ||
      this.selectedInsectGroupId === null
    ) {
      this.trappingResults = observable({});
      return;
    }

    this.loadingTrappingResults = true;

    const uri = new URI(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-results`
    );
    uri.addQuery('insect_group_id', this.selectedInsectGroupId);
    uri.addQuery(
      'trapping_zones[]',
      this.trappingZones.map((trappingZone) => trappingZone.id)
    );

    fetch(uri.toString(), {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${bearerToken}`,
        SeasonalDatabase: selectedDatabase
      }
    })
      .then((res) => {
        if (res.ok) return res.json();
        else if (res.status === 401) refreshBearerToken();
        else throw Error(res.statusText);
      })
      .then((data) => {
        if (data) {
          this.trappingResults = observable(data);
        }
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
        this.trappingResults = observable({});
      })
      .finally(() => {
        this.loadingTrappingResults = false;
      });
  }

  selectedDatabaseYear() {
    const split = this.selectedDatabase.split('_');
    return Number(split[split.length - 1]);
  }

  /**
   * Adds a new trap into the trapping zones list.
   *
   * @param {object} trap
   * @returns {boolean} Returns true if the trap was added. False otherwise.
   */
  addTrap(trap) {
    const trappingZoneIndex = this.trappingZones.findIndex(
      (trappingZone) => trappingZone.id === trap.trapping_zone_id
    );
    // Return if couldn't find the given trap's associated trapping zone.
    if (trappingZoneIndex === -1) return false;
    const newTrappingZones = this.trappingZones.map((tz, i) => {
      if (i !== trappingZoneIndex) return tz;
      // Append the given trap to the trap's associated trapping zone.
      return { ...tz, traps: [...tz.traps, trap] };
    });
    this.trappingZones = newTrappingZones;
    return true;
  }

  updateTrappingZones(selectedPropertyId) {
    fetch(`${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-zones/all`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + this.bearerToken,
        SeasonalDatabase: this.selectedDatabase
      },
      body: JSON.stringify({ zones: this.trappingZones })
    })
      .then((res) => {
        if (res.ok) {
          this.loadTrappingZones(
            this.bearerToken,
            this.refreshBearerToken,
            this.selectedDatabase,
            selectedPropertyId
          );
        } else if (res.status === 401) refreshBearerToken();
        else throw Error(res.statusText);
      })
      .catch((err) => {
        if (process.env.NODE_ENV === 'development') console.error(err);
      });
  }

  updateTrappingZone(
    trappingZone,
    selectedDatabase,
    bearerToken,
    refreshBearerToken
  ) {
    const id = trappingZone.id;
    return fetch(
      `${process.env.REACT_APP_TRAPLOG_API_URL}api/trapping-zones/${id}`,
      {
        method: 'PUT',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + bearerToken,
          SeasonalDatabase: selectedDatabase
        },
        body: JSON.stringify(trappingZone)
      }
    );
  }

  /**
   * Sets the selected insect group and then triggers the loadTrappingResults function to load the trapping results for this new insect group.
   *
   * @param {int} insectGroupId
   */
  setSelectedInsectGroupId(
    bearerToken,
    refreshBearerToken,
    selectedDatabase,
    insectGroupId
  ) {
    this.selectedInsectGroupId = insectGroupId;
    this.loadTrappingResults(bearerToken, refreshBearerToken, selectedDatabase);
  }

  /**
   *
   * @param {number} trappingZoneId
   * @param {boolean} highPressure
   * @returns void
   */
  updateTrappingZoneHighPressure(trappingZoneId, highPressure) {
    const trappingZoneIndex = this.trappingZones.findIndex(
      (tz) => tz.id === trappingZoneId
    );
    if (trappingZoneIndex === -1) return;
    const newTrappingZones = this.trappingZones;
    const trappingZone = this.trappingZones[trappingZoneIndex];
    trappingZone.high_pressure = highPressure;
    newTrappingZones[trappingZoneIndex] = trappingZone;
    runInAction(() => {
      this.trappingZones = JSON.parse(JSON.stringify(newTrappingZones));
    });
  }

  /**
   * Resets all trapping zones high pressure to false.
   * Set the 'high_pressure' property to false for all trapping zones.
   */
  resetAllTrappingZoneHighPressure() {
    const resetTrappingZones = this.trappingZones.map((tz) => ({
      ...tz,
      high_pressure: false
    }));
    runInAction(() => {
      this.trappingZones = JSON.parse(JSON.stringify(resetTrappingZones));
    });
  }

  /**
   * Sets the selected trapping zone.
   *
   * @param {int} trappingZoneId
   */
  setSelectedTrappingZoneId(trappingZoneId) {
    this.selectedTrappingZoneId = trappingZoneId;
  }

  resetTrapping() {
    this.loadingTrappingZones = false;
    this.trappingZones = [];
    this.loadingTrappingResults = false;
    this.trappingResults = {};
    this.selectedInsectGroupId = null;
    this.selectedTrappingZoneId = null;
  }

  isTrappingZoneHighRisk(trappingZone) {
    return trappingZone.cmsm && trappingZone.high_risk;
  }

  isTrappingZoneHighPressure(trappingZone) {
    return trappingZone.cmsm && trappingZone.high_pressure;
  }

  /**
   * Find the selected insect group model.
   *
   * @returns {object}
   */
  get selectedInsectGroup() {
    return this.insectGroups.find(
      (insectGroup) => insectGroup.id === this.selectedInsectGroupId
    );
  }

  /**
   * Find the selected trapping zone model.
   *
   * @returns {object}
   */
  get selectedTrappingZone() {
    return this.trappingZones.find(
      (trappingZone) => trappingZone.id === this.selectedTrappingZoneId
    );
  }

  /**
   * Find the trapping results for the selected trapping zone.
   *
   * @returns {object}
   */
  get selectedTrappingZoneResults() {
    return this.trappingResults[this.selectedTrappingZoneId];
  }

  /**
   * If a trapping zone is selected then find all traps that belong to the selected
   * trapping zone, otherwise find all traps.
   *
   * Also all traps must have a lure which attracts the selected insect group.
   *
   * @returns {array}
   */
  get traps() {
    if (this.trappingZones && this.selectedInsectGroupId !== null) {
      if (this.selectedTrappingZoneId === null) {
        return this.trappingZones
          .map((trappingZone) =>
            trappingZone.traps.filter((trap) =>
              trap.trap_lure.insects.some(
                (insect) =>
                  insect.insect_group.id === this.selectedInsectGroupId
              )
            )
          )
          .flat();
      } else {
        const trappingZone = this.trappingZones.find(
          (trappingZone) => trappingZone.id === this.selectedTrappingZoneId
        );

        return trappingZone
          ? trappingZone.traps.filter((trap) =>
              trap.trap_lure.insects.some(
                (insect) =>
                  insect.insect_group.id === this.selectedInsectGroupId
              )
            )
          : null;
      }
    } else {
      return null;
    }
  }

  /**
   * Returns the trappingZones sorted by name.
   *
   * @returns {array}
   */
  get sortedTrappingZones() {
    return this.trappingZones.slice().sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });
  }
}
